/**
 * result_history に積む result_data を「変わった項目だけ」に間引く（#3322）。
 *
 * detectChangedFields はトップレベルキー単位で差分を取るため、result_data 内の
 * 値が 1 つでも変わると changed_fields / previous_values の両方に result_data
 * 全体が入る。明細が多い帳票では 1 履歴で 1MB を超え、保存のたびに数 MB の
 * 読み書きが発生していた。
 *
 * 構造（`{ result_data: {...} }` の形）は変えず、中身の項目だけを落とす。
 * 読み手（精度集計の extractResultData / isOcrInitialEdit、CREATE 履歴を見る
 * 提案・画面）は形に依存しているため、形を保つことで旧形式の履歴と混在できる。
 *
 * 明細（配列）項目は行単位で間引くと行インデックスがずれ、精度集計の
 * パス突き合わせ（`8[12].8-3`）が壊れる。そのため配列は行数を保ったまま、
 * 変わった行だけ中身を残して他の行は空オブジェクトにする。
 *
 * Functions 側にも同じ実装がある（functions/src/utils/resultDataDiff.ts）。
 * 履歴の保存経路が Functions（OCRパイプライン）とフロント（ユーザー編集）に
 * 分かれており、どちらから保存しても同じ形になるよう両方に置いている。
 * 片方だけ直すと経路によって履歴の形が変わるため、必ず両方に入れる。
 */

/** 明細の行インデックスを保つためのプレースホルダ。 */
const EMPTY_ROW: Record<string, never> = {};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * 2 つの値が等しいかを判定する（JSON 比較による深い比較）。
 * Firestore から読んだ値同士の比較を想定しており、関数や循環参照は含まれない。
 */
const isSameValue = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) {
    // null と undefined は履歴上どちらも「値なし」として扱う
    return (a ?? null) === (b ?? null);
  }
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

/**
 * 明細（配列）項目を、行数を保ったまま変わった行だけ残す形に間引く。
 * 行が増減した場合は行インデックスの対応が崩れるため、間引かずそのまま返す。
 */
const pruneRows = (
  previousRows: unknown[],
  newRows: unknown[],
): { previous: unknown[]; next: unknown[] } | null => {
  if (previousRows.length !== newRows.length) return null;

  const previous: unknown[] = [];
  const next: unknown[] = [];
  let changed = false;

  for (let i = 0; i < newRows.length; i++) {
    const previousRow = previousRows[i];
    const newRow = newRows[i];
    if (isSameValue(previousRow, newRow)) {
      previous.push(EMPTY_ROW);
      next.push(EMPTY_ROW);
      continue;
    }
    changed = true;
    previous.push(previousRow ?? null);
    next.push(newRow ?? null);
  }

  return changed ? { previous, next } : null;
};

/**
 * result_data の変更前後を、変わった項目だけに間引いて返す。
 * 変更が無ければ null を返す（呼び出し側で履歴に含めない判断に使える）。
 */
export const pruneResultDataDiff = (
  previousResultData: unknown,
  newResultData: unknown,
): { previous: Record<string, unknown>; next: Record<string, unknown> } | null => {
  // 片方でも result_data の形でなければ間引けないので、そのまま扱わせる
  if (!isPlainObject(newResultData)) return null;
  // 変更前が result_data を持たない＝OCR の初回書き込み。
  // ここで間引くと previous が { "1": null, ... } になり、
  // patternAccuracyHandler の isOcrInitialEdit（previous_values.result_data が
  // null/空なら除外）が効かなくなって精度が 0% に落ちる。
  // 従来どおりの形のまま呼び出し側に任せる。
  if (!isPlainObject(previousResultData)) return null;
  const previous = previousResultData;

  const prunedPrevious: Record<string, unknown> = {};
  const prunedNext: Record<string, unknown> = {};
  let hasChange = false;

  for (const key of Object.keys(newResultData)) {
    const previousValue = previous[key];
    const newValue = newResultData[key];
    if (isSameValue(previousValue, newValue)) continue;

    if (Array.isArray(previousValue) && Array.isArray(newValue)) {
      const rows = pruneRows(previousValue, newValue);
      if (rows) {
        prunedPrevious[key] = rows.previous;
        prunedNext[key] = rows.next;
        hasChange = true;
        continue;
      }
      // 行数が変わった場合は行の対応が取れないため全行を残す
      prunedPrevious[key] = previousValue;
      prunedNext[key] = newValue;
      hasChange = true;
      continue;
    }

    prunedPrevious[key] = previousValue ?? null;
    prunedNext[key] = newValue ?? null;
    hasChange = true;
  }

  // 削除された項目（新データに無いキー）も変更として残す
  for (const key of Object.keys(previous)) {
    if (key in newResultData) continue;
    prunedPrevious[key] = previous[key] ?? null;
    prunedNext[key] = null;
    hasChange = true;
  }

  return hasChange ? { previous: prunedPrevious, next: prunedNext } : null;
};
